/**
 * Fetches BOTH ranges' country series and composes the aligned buckets + totals the chart
 * consumes (reuses the dashboard compare aligners). One provider only (the country's).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import type { TrackerId } from '@trafficpulse/shared';

import { alignBuckets } from '@/features/dashboard/compare/data/alignBuckets';
import { deriveTotals } from '@/features/dashboard/compare/data/deriveTotals';
import type { DashboardDateRange, Granularity } from '@/features/dashboard/compare/data/types';

import { useCountryTimeSeries } from './useCountryTimeSeries';

export function useCountryCompareSeries(params: {
  countryKey: string;
  campaignId?: string;
  provider?: TrackerId;
  rangeA: DashboardDateRange;
  rangeB: DashboardDateRange;
  granularity: Granularity;
  enabled: boolean;
}) {
  const { countryKey, campaignId, provider, rangeA, rangeB, granularity, enabled } = params;
  const base = { countryKey, campaignId, provider, granularity, enabled };
  const a = useCountryTimeSeries({ ...base, range: rangeA });
  const b = useCountryTimeSeries({ ...base, range: rangeB });

  const aligned = useMemo(
    () => alignBuckets(a.data?.buckets, b.data?.buckets),
    [a.data, b.data],
  );
  const totalsA = useMemo(() => deriveTotals(a.data?.buckets), [a.data]);
  const totalsB = useMemo(() => deriveTotals(b.data?.buckets), [b.data]);
  const currency =
    a.data?.buckets?.[0]?.currency ?? b.data?.buckets?.[0]?.currency ?? 'USD';
  const countA = a.data?.buckets.length ?? 0;
  const countB = b.data?.buckets.length ?? 0;

  return {
    aligned,
    totalsA,
    totalsB,
    currency,
    isLoading: a.isLoading || b.isLoading,
    isError: a.isError && b.isError,
    hasData: countA + countB > 0,
    refetch: () => {
      void a.refetch();
      void b.refetch();
    },
  };
}
